import { useCallback } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useMutation } from 'react-relay';
import { JiraIssuefieldvalueAri } from '@atlassian/ari/jira';
import FetchError, { ValidationError } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { fg } from '@atlassian/jira-feature-gating';

import {
	startCapturingTraceIds,
	stopCapturingTraceIds,
	getTraceIds,
} from '@atlassian/relay-traceid';
import type {
	useSaveSummaryFieldMutation,
	useSaveSummaryFieldMutation$data as useSaveSummaryFieldMutationData,
} from '@atlassian/jira-relay/src/__generated__/useSaveSummaryFieldMutation.graphql';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { StringValue } from '../types.tsx';
import type { SaveField } from './types.tsx';

const MUTATION_NAME =
	'singleLineTextHeading_issueFieldSingleLineTextHeading_SingleLineTextHeadingField_Mutation';

export const useSaveSummaryField = (issueId: string | undefined): SaveField => {
	const cloudId = useCloudId();

	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [commit] = useMutation<useSaveSummaryFieldMutation>(graphql`
		mutation useSaveSummaryFieldMutation($input: JiraUpdateSingleLineTextFieldInput!)
		@raw_response_type {
			jira {
				updateSingleLineTextField(input: $input) @optIn(to: "JiraIssueFieldMutations") {
					success
					errors {
						extensions {
							statusCode
						}
						message
					}
					field {
						...singleLineTextHeading_issueFieldSingleLineTextInlineEditHeading_SingleLineTextHeadingInlineEditView
					}
				}
			}
		}
	`);

	return useCallback(
		async (_issueKey: IssueKey, fieldId: string, newValue: StringValue) =>
			new Promise((resolve, reject) => {
				let JiraFieldAri = '';
				if (fg('edit_issue_summary_slo_error_logging')) {
					startCapturingTraceIds(MUTATION_NAME);
					try {
						JiraFieldAri = JiraIssuefieldvalueAri.create({
							siteId: cloudId,
							issueId: issueId || '',
							fieldId,
						}).toString();
					} catch (error: unknown) {
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						return reject(new ValidationError((error as Error).message));
					}
				}

				commit({
					variables: {
						input: {
							id: fg('edit_issue_summary_slo_error_logging')
								? JiraFieldAri
								: JiraIssuefieldvalueAri.create({
										siteId: cloudId,
										issueId: issueId || '',
										fieldId,
									}).toString(),
							operation: {
								operation: 'SET',
								text: newValue,
							},
						},
					},
					onCompleted(response: useSaveSummaryFieldMutationData) {
						if (response.jira?.updateSingleLineTextField) {
							const { success, errors: responseErrors } = response.jira?.updateSingleLineTextField;
							if (success) {
								resolve();
								return;
							}
							let traceIds: string[] = [];
							if (fg('edit_issue_summary_slo_error_logging')) {
								traceIds = getTraceIds(MUTATION_NAME);
								stopCapturingTraceIds(MUTATION_NAME);
							}

							if (responseErrors?.length) {
								// passing the status code
								if (
									responseErrors?.[0].extensions?.statusCode &&
									fg('edit_issue_summary_slo_error_logging')
								) {
									const errorWithTraceId = new FetchError(
										responseErrors?.[0].extensions?.statusCode,
										responseErrors?.[0].message || 'Unknown error when mutating summary',
										traceIds?.[0],
									);
									return reject(errorWithTraceId);
								}
								reject(
									responseErrors[0].message
										? new ValidationError(responseErrors[0].message)
										: new Error('Unknown error when mutating summary'),
								);
							}
						} else {
							reject(new Error('Unknown error when mutating summary'));
						}
					},
					onError(error: Error) {
						if (fg('edit_issue_summary_slo_error_logging')) {
							stopCapturingTraceIds(MUTATION_NAME);
						}
						reject(error);
					},
				});
			}),
		[cloudId, commit, issueId],
	);
};
